import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';

interface CoverageBarProps {
  /** Coverage percentage 0-100 */
  value: number;
  /** Show percentage text. Default: true */
  showLabel?: boolean;
  /** Bar height in px. Default: 6 */
  height?: number;
}

/**
 * CoverageBar — segmented progress bar with status-based coloring.
 * Uses theme-aware colors with a subtle glow at higher saturation.
 */
export default function CoverageBar({ value, showLabel = true, height = 6 }: CoverageBarProps) {
  const theme = useTheme();

  const color =
    value >= 95
      ? theme.palette.success.main
      : value >= 80
        ? theme.palette.warning.main
        : theme.palette.error.main;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
      <Box
        sx={{
          flex: 1,
          height,
          bgcolor: alpha(color, 0.12),
          borderRadius: height / 2,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            height: '100%',
            width: `${Math.min(value, 100)}%`,
            bgcolor: color,
            borderRadius: height / 2,
            transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: value >= 80 ? `0 0 8px ${alpha(color, 0.4)}` : 'none',
          }}
        />
      </Box>
      {showLabel && (
        <Typography
          sx={{
            minWidth: 38,
            textAlign: 'right',
            fontFamily: 'var(--font-mono)',
            fontSize: '0.75rem',
            fontWeight: 600,
            color,
          }}
        >
          {value}%
        </Typography>
      )}
    </Box>
  );
}
